import React from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Container } from 'components/base';
import {
  CloudinaryPicture,
  ScriptMarkupFaq,
  getCloudinaryDprSrcSet,
  getCloudinaryUrl,
} from 'components/rich';

import { Picture, Responsive, ctaTracker, productImage } from '../landing/shared';
import { Head, Layout } from '../layout';
import PdpIcon from '../product/pdpIcon';
import { Dots, FAQ_COMMON, Faq, HeroFacts, HowItWorks, TERMS_HREF } from './shared';

// Public /refer-friend for signed-out visitors (docs/refer-friend.md). Copy follows the Pencil
// frames "Refer a Friend — New Page" / "— Mobile". Every CTA goes to sign-in, which returns to
// /refer-friend, where the signed-in page shows the real code.
const SIGN_IN_HREF = `/signin?redirect=${encodeURIComponent('/refer-friend')}`;

const track = ctaTracker('refer_friend', { signed_in: false });

const HERO_IMAGE = { imageKey: 'hero/referfriend' };
const HERO_SIZES = '(min-width: 992px) 620px, calc(100vw - 40px)';

// Cap from the backend (User::MAX_REFERRAL_USAGE), $10 credit each.
const FRIENDS = 50;
const EXAMPLE_EARNED = 12;

const TILES = [
  {
    name: 'Square Prints',
    href: '/square-prints',
    image: productImage('square-prints/1'),
    text: 'Thick matte 4x4 prints of the photos they keep scrolling back to.',
  },
  {
    name: 'Classic Prints',
    href: '/classic-prints',
    image: productImage('classic-prints/1'),
    text: '4x6 prints with or without a white border, made for albums and frames.',
  },
  {
    name: 'Engineer Prints',
    href: '/engineer-prints',
    image: productImage('engineer-prints/3'),
    text: 'Large, affordable prints that hang from Wood Poster Rails.',
  },
];

// `description` is HTML, the FAQPage JSON-LD gets it without tags.
const FAQ = [
  ...FAQ_COMMON,
  {
    caption: 'Where do I find my code?',
    description:
      'Sign in and open Refer a Friend. Your balance and history are under Parabo Credit in the account menu.',
  },
];

const FAQ_JSON_LD = FAQ.map(({ caption, description }) => ({
  question: caption,
  answer: description.replace(/<[^>]+>/g, ''),
}));

const SignInButton = ({ location, className }) => (
  <Link
    href={SIGN_IN_HREF}
    className={classNames('lnd-btn', className)}
    onClick={track('get_code', location, '/signin')}
  >
    Get your referral code
    <PdpIcon name="arrowRight" size={18} />
  </Link>
);

SignInButton.propTypes = {
  location: PropTypes.string.isRequired,
  className: PropTypes.string,
};

// Illustration only: a sample code with the share actions the signed-in page offers.
const CodeCard = () => (
  <div className="rf-code-card" aria-hidden="true">
    <p className="rf-code-label">Your code</p>
    <div className="rf-code-row">
      <span className="rf-code">MAYA10</span>
      <span className="rf-code-copy">
        <PdpIcon name="copy" size={16} />
        Copy
      </span>
    </div>
    <div className="rf-code-share">
      {[
        ['messageCircle', 'Text'],
        ['mail', 'Email'],
        ['link', 'Link'],
      ].map(([icon, label]) => (
        <span key={label}>
          <PdpIcon name={icon} size={15} />
          {label}
        </span>
      ))}
    </div>
  </div>
);

const Hero = () => (
  <section className="rf-hero">
    <Container className="rf-hero-inner">
      <div className="rf-hero-top">
        <p className="rf-eyebrow">Refer a Friend</p>
        <h1 className="rf-h1">Give $10, get $10</h1>
        <p className="rf-lead">
          Know someone with a camera roll full of photos? Share your code. They get $10 off their
          first Parabo Press order, and you get $10 in Parabo credit once it ships.
        </p>
      </div>

      <div className="rf-scene">
        <div className="rf-scene-photo">
          <CloudinaryPicture imageKey={HERO_IMAGE.imageKey} sizes={HERO_SIZES} priority />
        </div>
        <CodeCard />
      </div>

      <div className="rf-hero-actions">
        <SignInButton location="hero" />
        <p className="rf-signin-note">Sign in or create a free account to see it.</p>
      </div>

      <HeroFacts />
    </Container>
  </section>
);

const UpTo500 = () => (
  <section className="rf-section rf-cream">
    <Container className="rf-split">
      <div>
        <p className="rf-eyebrow">How far it goes</p>
        <h2 className="rf-h2">Fifty friends, up to $500 in credit</h2>
        <p className="rf-body">
          Your code works for 50 first orders. Every one that ships adds $10 to your Parabo credit.
          <span className="lnd-desk">
            {' '}
            Save it up for a big Engineer Print or spend it as it comes in.
          </span>
        </p>
      </div>

      <figure className="rf-tracker">
        <div className="rf-tracker-head">
          <p className="rf-tracker-earned">
            <span className="rf-tracker-label">Earned so far</span>
            <span className="rf-tracker-value">${EXAMPLE_EARNED * 10}</span>
          </p>
          <p className="rf-tracker-of">
            <Responsive
              desktop={`${EXAMPLE_EARNED} of ${FRIENDS} friends · $${(FRIENDS - EXAMPLE_EARNED) * 10} to go`}
              phone={`${EXAMPLE_EARNED} of ${FRIENDS} friends`}
            />
          </p>
        </div>
        <Dots total={FRIENDS} filled={EXAMPLE_EARNED} />
        <figcaption className="rf-tracker-caption">
          <Responsive
            desktop="Example. Each dot is one friend and $10 in credit. Your real balance shows in Parabo Credit when you’re signed in."
            phone="Example. Each dot is one friend and $10 in credit."
          />
        </figcaption>
      </figure>
    </Container>
  </section>
);

const SpendCredit = () => (
  <section className="rf-section">
    <Container>
      <div className="rf-spend-head">
        <div>
          <p className="rf-eyebrow">What $10 is for</p>
          <h2 className="rf-h2">Prints and display, for them and for you</h2>
        </div>
        <p className="rf-spend-note">
          Your friend’s $10 comes off their products, not shipping. Earned Parabo Credit can cover your order, including shipping.
        </p>
      </div>
      <ul className="rf-tiles">
        {TILES.map((tile) => (
          <li key={tile.name}>
            <Link
              href={tile.href}
              className="rf-tile"
              onClick={track('product', 'spend_credit', tile.href)}
            >
              <span className="rf-tile-photo">
                <Picture image={tile.image} sizes="(min-width: 992px) 384px, 270px" alt="" />
              </span>
              <span className="rf-tile-name">
                {tile.name}
                <PdpIcon name="arrowRight" size={16} />
              </span>
              <span className="rf-tile-text">{tile.text}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const FinalCta = () => (
  <section className="rf-section rf-cream rf-final">
    <Container className="rf-final-inner">
      <div>
        <h2 className="rf-h2">Somebody you know needs their photos off their phone</h2>
        <p className="rf-final-body">Get your code, send it once, and let the credit add up.</p>
      </div>
      <SignInButton location="final" />
    </Container>
  </section>
);

const ReferFriendLanding = () => (
  <>
    <Head
      title="Refer a Friend"
      description="Share your Parabo Press referral code. Your friend gets $10 off their first order, and you earn $10 in Parabo credit for each friend whose order ships, up to $500."
      image={getCloudinaryUrl({ imageKey: HERO_IMAGE.imageKey, format: 'jpg' })}
      heroImage={getCloudinaryUrl(HERO_IMAGE)}
      heroImageSrcSet={getCloudinaryDprSrcSet(HERO_IMAGE)}
    />
    <ScriptMarkupFaq items={FAQ_JSON_LD} />

    <Layout>
      <div className="lnd rf">
        <Hero />
        <HowItWorks />
        <UpTo500 />
        <SpendCredit />
        <Faq items={FAQ} onTermsClick={track('terms', 'faq', TERMS_HREF)} />
        <FinalCta />
      </div>
    </Layout>
  </>
);

export default ReferFriendLanding;
